import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Alert,
  Button,
  List,
  ListItem,
  Chip,
  CardMedia,
  Divider,
  IconButton,
  Grid,
} from '@mui/material';
import {
  Assignment as ProjectIcon,
  CheckCircle as CheckIcon,
  AccessTime as ClockIcon,
  Build as BuildIcon,
  PictureAsPdf,
  DescriptionOutlined,
  PhotoLibraryOutlined,
  AssignmentOutlined,
  Compare,
} from '@mui/icons-material';
import { Project } from '../types';
import { useTranslation } from 'react-i18next';
import { Loading } from './Loading';
import PdfViewer from './PdfViewer';

interface ProjectListProps {
  projects: Project[];
  loading: boolean;
  error: string | null;
  onProjectClick: (project: Project) => void;
  searchQuery?: string;
  isMobileDevice?: boolean;
}

const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  loading,
  error,
  onProjectClick,
  searchQuery,
  isMobileDevice = true,
}) => {
  const { t } = useTranslation();
  const [openPdf, setOpenPdf] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [pdfPath, setPdfPath] = useState<string | undefined>(undefined);
  const [pdfType, setPdfType] = useState<string | undefined>(undefined);

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    try {
      return new Intl.DateTimeFormat('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(dateString));
    } catch {
      return null;
    }
  };

  const safeValueToString = (val: any): string => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'object') {
      const keys = Object.keys(val);
      if (keys.length > 0) {
        const values = keys.map(k => {
          const v = val[k];
          return typeof v === 'string' ? `${k}: ${v}` : k;
        });
        return values.join(', ');
      }
      return JSON.stringify(val);
    }
    return String(val);
  };

  const getStatusIcon = (status?: string): React.ReactElement | undefined => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckIcon sx={{ fontSize: '16px' }} />;
      case 'pending':
        return <ClockIcon sx={{ fontSize: '16px' }} />;
      case 'progress':
        return <BuildIcon sx={{ fontSize: '16px' }} />;
      default:
        return undefined;
    }
  };

  const getStatusStyles = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return {
          backgroundColor: '#E8F5E8', // Light green background
          color: '#2E7D32', // Dark green text
          '& .MuiChip-icon': {
            color: '#2E7D32', // Dark green icon
          },
        };
      case 'pending':
        return {
          backgroundColor: '#FFF3E0', // Light orange background
          color: '#E65100', // Dark orange text
          '& .MuiChip-icon': {
            color: '#E65100', // Dark orange icon
          },
        };
      case 'progress':
        return {
          backgroundColor: '#E3F2FD', // Light blue background
          color: '#007BFF', // Dark blue text
          '& .MuiChip-icon': {
            color: '#007BFF', // Dark blue icon
          },
        };
      default:
        return {
          backgroundColor: '#F5F5F5',
          color: '#757575',
        };
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'progress':
        return t('status.progress');
      case 'completed':
        return t('status.completed');
      case 'pending':
        return t('status.pending');
      case 'cancelled':
        return t('status.cancelled');
      default:
        return t('status.unknown');
    }
  };

  const handlePdfClick = (event: React.MouseEvent, project: Project, pdfType: 'lanzamiento' | 'report' | 'image_book' | 'homologation' | 'series') => {
    event.stopPropagation(); // Prevent card click
    setSelectedProject(project);
    setOpenPdf(true);
    setPdfPath(
      pdfType === 'lanzamiento' ? project.gcs_pdf_path : 
      pdfType === 'report' ? project.gcs_report_path : 
      pdfType === 'homologation' ? project.gcs_homologation_book_path :
      pdfType === 'series' ? project.gcs_series_book_path :
      project.gcs_image_book_path
    );
    setPdfType(pdfType);
  };

  const handleClosePdf = () => {
    setOpenPdf(false);
    setSelectedProject(null);
  };

  // Loading state with skeletons
  if (loading) {
    return (
      <Loading />
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ px: 2, mt: 4 }}>
        <Alert 
          sx={{ borderRadius: '0px !important' }}
          severity="error"
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Empty state
  if (projects.length === 0) {
    return (
      <Box sx={{ px: 1, pt: 2 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ProjectIcon 
              sx={{ 
                fontSize: isMobileDevice ? 48 : 64, 
                color: 'text.secondary', 
                mb: 2 
              }} 
            />
            <Typography 
              variant={isMobileDevice ? "subtitle1" : "h6"} 
              color="text.secondary"
              gutterBottom
            >
              {searchQuery 
                ? t('home.search.noResults', { query: searchQuery })
                : t('home.projects.empty')
              }
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery 
                ? t('home.search.noResultsDescription')
                : t('home.projects.emptyDescription')
              }
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }
console.log("PATH", projects)
  // Projects list
  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12, lg: 16 }}>
        {projects.map((project, index) => (
          <Grid size={isMobileDevice ? 12 : 4} key={index}>
            <Card sx={{ width: '100%' }}>
              <CardActionArea
                onClick={() => onProjectClick(project)}
                sx={{
                  minHeight: isMobileDevice ? 88 : 72,
                  px: 0,
                  py: 0,
                }}
              >
                <Box sx={{ position: "relative", width: '100%', height: '75%', aspectRatio: '4/3' }}>
                  {/* Thumbnail image (shows under main) */}
                  {project.cover_thumbnail_photo && (
                    <img
                      src={project.cover_thumbnail_photo}
                      alt={t('projectList.photoOrder')}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        filter: 'blur(1px)', // subtle effect
                        zIndex: 0,
                      }}
                    />
                  )}
                  {/* Main image overlays thumbnail */}
                  <img
                    src={project.cover_photo || "/images/default-cover.png"}
                    alt={t('projectList.photoOrder')}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      // zIndex: 1,
                      transition: 'opacity 0.3s',
                    }}
                    loading="lazy"
                  />
                  <Chip
                    label={project.project_code}
                    color="secondary"
                    sx={{
                      position: "absolute",
                      top: 12,
                      left: 12,
                    }}
                  />
                  
                  {/* Top-right chip */}
                  <Chip
                    label={getStatusLabel(project.status)}
                    icon={getStatusIcon(project.status)}
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      ...getStatusStyles(project.status),
                    }}
                  />

                  {/* Homogando chip - shows when status_mediciones is "progress" */}
                  {project.status_mediciones === "progress" && (
                    <Chip
                      label="Homologación"
                      sx={{
                        position: "absolute",
                        top: 52,
                        right: 12,
                        backgroundColor: "warning.light", // Light blue background
                        color: "warning.dark", // Dark blue text
                      }}
                    />
                  )}

                  {(project.gcs_series_book_path) && (
                  <IconButton
                    onClick={(event) => handlePdfClick(event, project, 'series')}
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      right: 12+48+48+48+48,
                      backgroundColor: 'background.light',
                      '&:hover': {
                        backgroundColor: 'background.base',
                      },
                    }}
                  ><Compare /></IconButton>
                  )}
                  {(project.gcs_homologation_book_path) && (
                  <IconButton
                    onClick={(event) => handlePdfClick(event, project, 'homologation')}
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      right: 12+48+48+48,
                      backgroundColor: 'background.light',
                      '&:hover': {
                        backgroundColor: 'background.base',
                      },
                    }}
                  ><AssignmentOutlined /></IconButton>
                  )}
                  {(project.gcs_image_book_path) && (
                  <IconButton
                    onClick={(event) => handlePdfClick(event, project, 'image_book')}
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      right: 12+48+48,
                      backgroundColor: 'background.light',
                      '&:hover': {
                        backgroundColor: 'background.base',
                      },
                    }}
                  ><PhotoLibraryOutlined /></IconButton>
                  )}
                  {(project.gcs_report_path) && (
                  <IconButton
                    onClick={(event) => handlePdfClick(event, project, 'report')}
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      right: 12+48,
                      backgroundColor: 'background.light',
                      '&:hover': {
                        backgroundColor: 'background.base',
                      },
                    }}
                  ><DescriptionOutlined /></IconButton>
                  )}
                  <IconButton
                    onClick={(event) => handlePdfClick(event, project, 'lanzamiento')}
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      right: 12,
                      backgroundColor: 'background.light',
                      '&:hover': {
                        backgroundColor: 'background.base',
                      },
                    }}
                  ><PictureAsPdf /></IconButton>
                </Box>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 0 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 1,
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 1,
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2">
                            {/* text primary should be bold */}
                            <Box component="span" sx={{ color: 'text.primary', fontWeight: 'bold' }}>{t('projectList.cliente')} </Box>
                            <Box component="span" sx={{ color: 'primary.main' }}>{project["DETALLES DE LA ORDEN"]?.Cliente || '-'}</Box>
                          </Typography>
                          <Typography variant="body2">
                            <Box component="span" sx={{ color: 'text.primary', fontWeight: 'bold' }}>{t('projectList.marcaChasis')} </Box>
                            <Box component="span" sx={{ color: 'primary.main' }}>{safeValueToString(project["DETALLES DEL CHASIS"]?.Marca?.value)}</Box>
                          </Typography>
                          <Typography variant="body2">
                            <Box component="span" sx={{ color: 'text.primary', fontWeight: 'bold' }}>{t('projectList.modeloChasis')} </Box>
                            <Box component="span" sx={{ color: 'primary.main' }}>{safeValueToString(project["DETALLES DEL CHASIS"]?.Modelo?.value)}</Box>
                          </Typography>
                          <Typography variant="body2">
                            <Box component="span" sx={{ color: 'text.primary', fontWeight: 'bold' }}>{t('projectList.carrozado')} </Box>
                            <Box component="span" sx={{ color: 'primary.main' }}>{safeValueToString(project["DETALLES EQUIPO"]?.Modelo?.value)}</Box>
                          </Typography>
                          <Typography variant="body2">
                            <Box component="span" sx={{ color: 'text.primary', fontWeight: 'bold' }}>{t('projectList.capacidad')} </Box>
                            <Box component="span" sx={{ color: 'primary.main' }}>{safeValueToString(project["DETALLES DEL CHASIS"]?.["Capacidad Distribución OLTP"]?.value)}</Box>
                          </Typography>
                          <Typography variant="body2">
                            <Box component="span" sx={{ color: 'text.primary', fontWeight: 'bold' }}>{t('projectList.elevador')} </Box>
                            <Box component="span" sx={{ color: 'primary.main' }}>{safeValueToString(project["DETALLES EQUIPO"]?.["Tipo Elevador"]?.value)}</Box>
                          </Typography>
                          <Typography variant="body2">
                            <Box component="span" sx={{ color: 'text.primary', fontWeight: 'bold' }}>{t('projectList.unidades')} </Box>
                            <Box component="span" sx={{ color: 'primary.main' }}>{project["DETALLES DE LA ORDEN"]?.Unidades || '-'}</Box>
                          </Typography>
                        </Box>
                        
                      </Box>
                    </Box>
                    <Divider />
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}>
                    {project.lastModifiedAt && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          fontSize: isMobileDevice ? '13px' : '14px',
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {project.lastModifiedBy}
                      </Typography>
                    )}
                    {project.lastModifiedAt && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: isMobileDevice ? '11px' : '12px' }}
                      >
                        {formatDate(project.lastModifiedAt)}
                      </Typography>
                    )}
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
          ))}
      </Grid>
      {/* PDF Viewer Dialog */}
      <PdfViewer
        open={openPdf}
        onClose={handleClosePdf}
        pdfPath={pdfPath}
        title={
          pdfType === 'lanzamiento' ? t('projectList.launchTitle', { code: selectedProject?.project_code || '' }) : 
          pdfType === 'report' ? t('projectList.reportTitle', { code: selectedProject?.project_code || '' }) : 
          pdfType === 'homologation' ? t('projectList.homologationBookTitle', { code: selectedProject?.project_code || '' }) :
          pdfType === 'series' ? t('projectList.seriesBookTitle', { code: selectedProject?.project_code || '' }) :
          t('projectList.imageBookTitle', { code: selectedProject?.project_code || '' })
        }
      />
    </Box>
  );
};

export default ProjectList;
